import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

/* ── useInView: fires once when the element scrolls into view, then stays true ── */
function useInView(threshold = 0.4) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
}

/* ── CountUp: animates 0 -> value once its wrapper scrolls into view ── */
const CountUp = ({ value, suffix = "", duration = 3200 }) => {
  const [ref, inView] = useInView(0.4);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;
    let start = null;
    let frame;

    const step = (ts) => {
      if (start === null) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(value * eased));
      if (progress < 1) frame = requestAnimationFrame(step);
      else setDisplay(value);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [inView, value, duration]);

  return (
    <h3 ref={ref}>
      {display}
      {suffix}
    </h3>
  );
};

const STATS = [
  {
    value: 3000,
    suffix: "+",
    label: "Happy Clients",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: 100,
    suffix: "+",
    label: "Products Delivered",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 16.5c0 .38-.21.71-.53.88l-7.9 4.44c-.16.12-.36.18-.57.18s-.41-.06-.57-.18l-7.9-4.44A.991.991 0 0 1 3 16.5v-9c0-.38.21-.71.53-.88l7.9-4.44c.16-.12.36-.18.57-.18s.41.06.57.18l7.9 4.44c.32.17.53.5.53.88v9z" stroke="currentColor" strokeWidth="1.6" fill="none" />
        <path d="M3.27 6.96 12 12.01l8.73-5.05M12 22.08V12" stroke="currentColor" strokeWidth="1.6" fill="none" />
      </svg>
    ),
  },
  {
    value: 8,
    suffix: "+",
    label: "Years Experience",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2l2.9 6.26L21 9.27l-4.5 4.38L17.8 21 12 17.77 6.2 21l1.3-7.35L3 9.27l6.1-1.01L12 2z" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: 20,
    suffix: "+",
    label: "Industry Awards",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 15.5c3.31 0 6-2.69 6-6s-2.69-6-6-6-6 2.69-6 6 2.69 6 6 6zM8.21 13.89 7 22l5-3 5 3-1.21-8.12" stroke="currentColor" strokeWidth="1.6" fill="none" />
      </svg>
    ),
  },
];

const Hero = () => {
  return (
    <section className="hero-section">
      <div className="container">
        <div className="row align-items-center">

          {/* LEFT CONTENT */}
          <div className="col-lg-6 hero-left fade-in-left">
            <span className="hero-badge">Innovate • Integrate • Inspire</span>

            <h1>
              Welcome to <br />
              <span className="hero-brand-name">Sagveek Technologies Pvt. Ltd.</span>
            </h1>

            <p className="hero-description">
              We build smart digital solutions, robust software products and
              future-ready technology that empower businesses to innovate,
              scale and succeed in the digital world.
            </p>

            <div className="hero-buttons">
              <Link to="/products" className="btn-filled">
                Explore Our Products
                <span className="btn-arrow">→</span>
              </Link>
              <Link to="/about" className="btn-outline">
                Learn More About Us
                <span className="btn-arrow">→</span>
              </Link>
            </div>

            {/* STATS */}
            <div className="hero-stats">
              {STATS.map((s) => (
                <div className="stat-item" key={s.label}>
                  <div className="stat-icon">{s.icon}</div>
                  <div>
                    <CountUp value={s.value} suffix={s.suffix} />
                    <p>{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ================= HERO CSS ================= */}
      <style>{`
        .hero-section {
          position: relative;
          margin-top: 0;
          min-height: 600px;
          display: flex;
          align-items: center;
          background-image: url('/images/home/1.png');
          background-image: image-set(url('/images/home/1.avif') type('image/avif'), url('/images/home/1.webp') type('image/webp'), url('/images/home/1.png') type('image/png'));
          background-repeat: no-repeat;
          background-position: center;
          background-size: cover;
          padding: 0 0 80px;
          overflow: hidden;
        }

        .hero-badge {
          display: inline-block;
          background: #f4e8fc;
          color: #6C0A9F;
          font-size: 13px;
          font-weight: 600;
          padding: 6px 16px;
          border-radius: 30px;
          margin-top: 0;
          margin-bottom: 18px;
          letter-spacing: 0.3px;
        }

        .hero-left h1 {
          font-size: 44px;
          font-weight: 800;
          line-height: 1.25;
          margin-bottom: 18px;
          color: #14142b;
          text-shadow: 0 2px 12px rgba(255,255,255,0.8), 0 1px 2px rgba(255,255,255,0.9);
        }

        .hero-brand-name {
          color: #6C0A9F;
          font-family: 'Outfit', sans-serif;
          font-size: inherit;
          font-weight: inherit;
          letter-spacing: 0.3px;
          text-shadow: none;
        }

        .hero-description {
          font-size: 16px;
          color: #5b5b6b;
          margin-bottom: 30px;
          max-width: 520px;
          line-height: 1.6;
          text-shadow: 0 1px 8px rgba(255,255,255,0.9);
        }

        .hero-buttons {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
          margin-bottom: 45px;
        }

        .btn-filled,
        .btn-outline {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 26px;
          border-radius: 30px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .btn-filled {
          background: #6C0A9F;
          color: #fff;
          border: 2px solid #6C0A9F;
        }

        .btn-filled:hover {
          background: #55077d;
          border-color: #55077d;
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(108, 10, 159, 0.4);
          color: #fff;
        }

        .btn-outline {
          border: 2px solid #6C0A9F;
          color: #6C0A9F;
          background: #fff;
        }

        .btn-outline:hover {
          background: #6C0A9F;
          border-color: #6C0A9F;
          color: #fff;
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(108, 10, 159, 0.25);
        }

        .btn-arrow {
          font-size: 15px;
          transition: transform 0.25s ease;
        }

        .btn-filled:hover .btn-arrow,
        .btn-outline:hover .btn-arrow {
          transform: translateX(3px);
        }

        .hero-stats {
          display: flex;
          gap: 40px;
          flex-wrap: wrap;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .stat-icon {
          width: 30px;
          height: 30px;
          color: #6C0A9F;
        }

        .stat-icon svg {
          width: 100%;
          height: 100%;
        }

        .stat-item h3 {
          font-size: 20px;
          font-weight: 800;
          color: #14142b;
          margin: 0;
        }

        .stat-item p {
          font-size: 13px;
          color: #777;
          margin: 0;
        }

        .fade-in-left {
          opacity: 0;
          animation: fadeInLeft 1s forwards;
        }

        .fade-in-right {
          opacity: 0;
          animation: fadeInRight 1s forwards;
        }

        @keyframes fadeInLeft {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes fadeInRight {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @media (max-width: 768px) {
          .hero-section {
            min-height: auto;
            margin-top: 0;
            padding: 0 0 90px;
            text-align: center;
            background-image: url('/images/home/1.png');
            background-image: image-set(url('/images/home/1-960.avif') type('image/avif'), url('/images/home/1-960.webp') type('image/webp'), url('/images/home/1.png') type('image/png'));
            background-position: center;
            background-size: cover;
          }

          .hero-left {
            padding: 0;
          }

          .hero-left h1 {
            font-size: 32px;
          }

          .hero-brand-name {
            display: block;
          }

          .hero-description {
            margin-left: auto;
            margin-right: auto;
          }

          .hero-buttons,
          .hero-stats {
            justify-content: center;
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;